/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useState, useEffect, useCallback } from 'react';
import type { HomepageBlock } from '../models/homepageBlockTypes';
import {
  isProductTilesBlock,
  isSpotlightCardsBlock,
  isNotificationBannerBlock,
} from '../models/homepageBlockHelpers';

interface UseHomepageLoadingRegistryProps {
  blocks?: HomepageBlock[];
  isWeb: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  onAllModulesLoaded?: () => void;
}

export const useHomepageLoadingRegistry = ({
  blocks,
  isWeb,
  isLoading,
  isSuccess,
  onAllModulesLoaded,
}: UseHomepageLoadingRegistryProps) => {
  const [registeredModules, setRegisteredModules] = useState<Set<string>>(
    new Set(),
  );
  const [loadedModules, setLoadedModules] = useState<Set<string>>(new Set());
  const [isRegistryInitialized, setIsRegistryInitialized] = useState(false);
  const [hasCalledLoadedCallback, setHasCalledLoadedCallback] = useState(false);

  const isBlocksReady = !isLoading && isSuccess;

  /**
   * Initialize Registry
   * Identifies which modules are expected to load based on the fetched blocks configuration.
   * This ensures we wait for exactly what will be rendered on the current platform.
   */
  useEffect(() => {
    if (isBlocksReady && !isRegistryInitialized && blocks) {
      const modules = new Set<string>();

      // Header is only for Native Mobile
      if (!isWeb) {
        modules.add('header');
      }

      // Register dynamic blocks that support loading reporting
      blocks.forEach((b) => {
        if (isSpotlightCardsBlock(b)) modules.add(b.uuid);
        else if (isProductTilesBlock(b)) modules.add(b.uuid);
        else if (isNotificationBannerBlock(b)) modules.add(b.uuid);
      });

      setRegisteredModules(modules);
      setIsRegistryInitialized(true);
    }
  }, [isBlocksReady, isRegistryInitialized, blocks, isWeb]);

  /**
   * Callback passed to child components to report their data loading completion.
   * Supports both async (e.g. Spotlight) and sync (e.g. ProductTiles) modules.
   */
  const handleModuleLoaded = useCallback((id: string) => {
    setLoadedModules((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  /**
   * Monitor Registry Progress
   * Triggers the final 'onAllModulesLoaded' callback when all expected modules
   * have reported completion.
   */
  useEffect(() => {
    if (hasCalledLoadedCallback) return;
    if (!isRegistryInitialized) return;

    const allLoaded = Array.from(registeredModules).every((id) =>
      loadedModules.has(id),
    );

    if (allLoaded) {
      if (onAllModulesLoaded) {
        onAllModulesLoaded();
        setHasCalledLoadedCallback(true);
      }
    }
  }, [
    isRegistryInitialized,
    registeredModules,
    loadedModules,
    hasCalledLoadedCallback,
    onAllModulesLoaded,
  ]);

  return {
    handleModuleLoaded,
  };
};
